import React, { useState, useMemo, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { PRODUCTS, PRODUCT_CATEGORIES, REVIEWS } from "../data";
import ProductCard from "../components/ProductCard";
import Rating from "../components/Rating";
import PressStrip from "../components/PressStrip";
import { Star } from "lucide-react";

export default function Shop({ onAdd, onBuyNow }) {
  const [params, setParams] = useSearchParams();
  const initial = params.get("cat");
  const [cat, setCat] = useState(initial && PRODUCT_CATEGORIES.includes(initial) ? initial : "All");
  const [sort, setSort] = useState("featured");

  // Keep state in sync if URL query changes (deep links from nav dropdown)
  useEffect(() => {
    const q = params.get("cat");
    if (q && PRODUCT_CATEGORIES.includes(q) && q !== cat) setCat(q);
    if (!q && cat !== "All") setCat("All");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const onPickCategory = (c) => {
    setCat(c);
    if (c === "All") setParams({});
    else setParams({ cat: c });
  };

  const list = useMemo(() => {
    let arr = cat === "All" ? PRODUCTS : PRODUCTS.filter((p) => p.category === cat);
    if (sort === "price-asc") arr = [...arr].sort((a, b) => a.price - b.price);
    else if (sort === "price-desc") arr = [...arr].sort((a, b) => b.price - a.price);
    else if (sort === "rating") arr = [...arr].sort((a, b) => (b.rating || 0) - (a.rating || 0));
    return arr;
  }, [cat, sort]);

  return (
    <div data-testid="shop-page" className="relative">
      <span className="stamp-num absolute top-6 right-6 md:right-12">N° 02</span>

      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-12 md:pt-16 pb-14">
        <p className="section-num">
          <strong>02</strong> Shop the harvest
        </p>
        <div className="grid md:grid-cols-12 gap-10 items-end mt-5">
          <h1 className="md:col-span-8 font-display text-display-lg text-ink tracking-tightest fade-up">
            Today's harvest,
            <br />
            <span className="font-italic text-coral">ready to ship.</span>
          </h1>
          <p className="md:col-span-4 text-ink/60 max-w-md text-[15px] leading-relaxed fade-up-d1">
            Fresh from our Nagpur unit. Every product can be ordered instantly
            on WhatsApp — pay on delivery in Nagpur, or by UPI for elsewhere.
          </p>
        </div>

        {/* Filter + sort row */}
        <div className="mt-12 flex flex-wrap gap-4 items-center justify-between">
          <div className="flex flex-wrap gap-2.5" data-testid="shop-filters">
            {PRODUCT_CATEGORIES.map((c) => {
              const active = c === cat;
              return (
                <button
                  key={c}
                  data-testid={`filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => onPickCategory(c)}
                  className={`rounded-full px-5 py-2.5 text-sm font-medium border transition-all ${
                    active
                      ? "bg-ink text-bone border-ink"
                      : "bg-bone text-ink border-ink/15 hover:border-coral hover:text-coral"
                  }`}
                >
                  {c}
                  <span className={`ml-2 text-[11px] tabular-nums ${active ? "text-bone/60" : "text-muted"}`}>
                    {c === "All"
                      ? PRODUCTS.length
                      : PRODUCTS.filter((p) => p.category === c).length}
                  </span>
                </button>
              );
            })}
          </div>

          <label className="inline-flex items-center gap-3 text-sm">
            <span className="text-[10px] uppercase tracking-[0.24em] text-muted">Sort by</span>
            <select
              data-testid="shop-sort"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="bg-bone border border-ink/15 rounded-full px-4 py-2 outline-none focus:border-coral text-ink"
            >
              <option value="featured">Featured</option>
              <option value="price-asc">Price · low to high</option>
              <option value="price-desc">Price · high to low</option>
              <option value="rating">Top rated</option>
            </select>
          </label>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-20">
        {list.length === 0 ? (
          <p className="text-ink/60 text-center py-20">Nothing in this category yet.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {list.map((p, idx) => (
              <ProductCard
                key={p.id}
                product={p}
                onAdd={onAdd}
                onBuyNow={onBuyNow}
                index={idx}
                total={list.length}
              />
            ))}
          </div>
        )}
      </section>

      {/* Reviews preview by product */}
      <section className="bg-sageFog border-y border-ink/10 py-24 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-8 items-end">
            <h2 className="md:col-span-8 font-display text-display-md text-ink tracking-tightest">
              Recent reviews,
              <br />
              <span className="font-italic text-coral">straight from the kitchen.</span>
            </h2>
            <div className="md:col-span-4 flex md:justify-end items-center gap-2">
              <div className="inline-flex items-center gap-1">
                {[0, 1, 2, 3, 4].map((i) => (
                  <Star key={i} className="h-4 w-4 fill-butter text-butter" strokeWidth={1.5} />
                ))}
              </div>
              <span className="text-sm font-medium tabular-nums">4.8</span>
              <span className="text-sm text-muted">(751 reviews)</span>
            </div>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Object.entries(REVIEWS).slice(0, 6).flatMap(([pid, revs]) => {
              const product = PRODUCTS.find((p) => p.id === pid);
              if (!product) return [];
              const r = revs[0];
              return [
                <article
                  key={pid}
                  className="bg-boneSoft rounded-3xl p-6 md:p-7 border border-ink/5 shadow-soft"
                >
                  <div className="flex items-start justify-between gap-3">
                    <Rating value={r.rating} showCount={false} />
                    <span className="text-[10px] uppercase tracking-[0.22em] text-muted">
                      {new Date(r.date).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
                    </span>
                  </div>
                  <h3 className="font-display text-[22px] mt-3 text-ink leading-snug tracking-tightest">
                    "{r.title}"
                  </h3>
                  <p className="mt-3 text-ink/65 text-sm leading-relaxed">{r.body}</p>
                  <div className="mt-5 pt-4 border-t border-ink/5 flex items-center justify-between">
                    <p className="text-[13px] font-medium text-ink">{r.name}</p>
                    <span className="text-[11px] uppercase tracking-[0.22em] text-coral">
                      {product.name}
                    </span>
                  </div>
                </article>,
              ];
            })}
          </div>
        </div>
      </section>

      <PressStrip variant="light" />
    </div>
  );
}
